'use client'

import { useRef, type ReactNode } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { Code, Database, Globe, Layers, Rocket, Shield, Sparkles, Users, Zap, Box, Cpu } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback, Orbital } from '@mdxui/primitives'
import { InstallSnippet } from '@mdxui/primitives/install-snippet'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  reducedMotionVariants,
} from '../../lib/motion'

/**
 * CtaOrbitingInstall - CTA with orbiting elements and install snippet
 *
 * Features animated orbiting circles around a centered CTA with an InstallSnippet.
 * Supports both avatars (for community/social) and icons (for features/tech).
 * Great for developer tools, CLI packages, and SDK installation CTAs.
 *
 * **CRITICAL: Use this variant for ANY npm/npx/pnpm/yarn/bun install commands**
 * For regular buttons, use CtaOrbiting instead.
 */

export interface OrbitItem {
  /** Type of orbit item */
  type: 'avatar' | 'icon'
  /** Avatar image source URL (for type: 'avatar') */
  src?: string
  /** Fallback text/initials (for type: 'avatar') */
  fallback?: string
  /** Icon element (for type: 'icon') */
  icon?: ReactNode
  /** Alt text for accessibility */
  alt?: string
}

type PackageManager = 'npm install' | 'npm i' | 'npx' | 'pnpm add' | 'pnpm dlx' | 'yarn add' | 'bun add' | 'bunx'

/**
 * Props for CtaOrbitingInstall component
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Centered layout with animated orbiting elements (avatars or icons)
 * - Uses InstallSnippet component ONLY - NO regular buttons
 * - **CRITICAL: Use this for npm/npx/pnpm/yarn/bun install commands with orbiting animation**
 * - Best for developer tools, CLI packages, SDK installation CTAs
 * - Supports 3 orbit levels (inner, middle, outer)
 *
 * **CTA Button Rules (CRITICAL):**
 * 1. Use CtaOrbiting for 1 regular button (NOT install commands)
 * 2. Use CtaOrbitingInstall for InstallSnippet ONLY (this component)
 * 3. NEVER mix InstallSnippet with regular buttons - they are mutually exclusive
 */
export interface CtaOrbitingInstallProps {
  /** Main heading text */
  heading?: string
  /** Description text */
  description?: string
  /** Package name for the install command (e.g., "mdxui", "@mdxui/cli") */
  package: string
  /** Package manager command prefix. Default: "npm install" */
  command?: PackageManager
  /** Inner orbit items (closest to center) */
  innerOrbit?: OrbitItem[]
  /** Middle orbit items */
  middleOrbit?: OrbitItem[]
  /** Outer orbit items (furthest from center) */
  outerOrbit?: OrbitItem[]
  className?: string
}

// Default icons representing a developer platform/community
const defaultInnerOrbit: OrbitItem[] = [
  { type: 'icon', icon: <Code className="size-5" /> },
  { type: 'icon', icon: <Database className="size-5" /> },
  { type: 'icon', icon: <Globe className="size-5" /> },
]

const defaultMiddleOrbit: OrbitItem[] = [
  { type: 'icon', icon: <Rocket className="size-5" /> },
  { type: 'icon', icon: <Shield className="size-5" /> },
  { type: 'icon', icon: <Sparkles className="size-5" /> },
]

const defaultOuterOrbit: OrbitItem[] = [
  { type: 'icon', icon: <Users className="size-5" /> },
  { type: 'icon', icon: <Zap className="size-5" /> },
  { type: 'icon', icon: <Layers className="size-5" /> },
  { type: 'icon', icon: <Box className="size-5" /> },
  { type: 'icon', icon: <Cpu className="size-5" /> },
]

function OrbitItemRenderer({ item }: { item: OrbitItem }) {
  if (item.type === 'avatar') {
    return (
      <Avatar className="size-10">
        {item.src && <AvatarImage src={item.src} alt={item.alt || ''} />}
        <AvatarFallback className="text-xs">{item.fallback}</AvatarFallback>
      </Avatar>
    )
  }

  // Icon type
  return (
    <div className="flex size-10 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm">
      {item.icon}
    </div>
  )
}

/**
 * CtaOrbitingInstall - CTA with orbiting elements and install snippet
 *
 * **Layout:**
 * ```
 * ─────────────────────────────────────
 *         ○ ○    ○    ○ ○
 *       ○               ○
 *      ○     Heading      ○
 *     ○    Description     ○
 *      ○  [npm install x]  ○
 *       ○               ○
 *         ○ ○    ○    ○ ○
 * ─────────────────────────────────────
 * ```
 *
 * **CRITICAL: When to Use This Component:**
 * - Use for install commands with orbiting visual effect
 * - Use CtaOrbiting for regular buttons
 * - Use CtaCenteredCheck for install commands without orbiting effect
 *
 * @example
 * ```tsx
 * <CtaOrbitingInstall
 *   heading="Ship Faster with Smarter RPC"
 *   description="Stop fighting network latency."
 *   package="capnweb"
 *   command="npm install"
 * />
 * ```
 */
const CtaOrbitingInstall = ({
  heading = 'Get Started Today',
  description = 'Install the package and start building amazing experiences.',
  package: packageName,
  command = 'npm install',
  innerOrbit = defaultInnerOrbit,
  middleOrbit = defaultMiddleOrbit,
  outerOrbit = defaultOuterOrbit,
  className,
}: CtaOrbitingInstallProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto relative">
        <div className="flex w-full items-center justify-center">
          <div className="absolute z-10 flex h-full w-full flex-col items-center justify-center gap-4 px-4">
            <div className="pointer-events-none absolute inset-y-0 left-1/2 top-1/2 h-1/3 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 bg-background blur-2xl" />
            <motion.h1
              className="z-10 text-center text-5xl text-balance font-semibold tracking-tighter sm:text-6xl"
              variants={headerAnimation}
            >
              {heading}
            </motion.h1>
            <motion.div
              className="z-10 max-w-2xl text-center"
              variants={headerAnimation}
            >
              <Markdown size="base">{description}</Markdown>
            </motion.div>

            <motion.div className="z-10 w-full sm:w-auto flex justify-center" variants={headerAnimation}>
              <InstallSnippet
                package={packageName}
                command={command}
                className="w-full sm:w-auto"
              />
            </motion.div>
          </div>
          <div className="relative flex h-[620px] w-full flex-col items-center justify-center overflow-hidden">
            <Orbital itemSize={40} radius={130} speed={2}>
              {innerOrbit.map((item, index) => (
                <OrbitItemRenderer key={index.toString()} item={item} />
              ))}
            </Orbital>
            <Orbital itemSize={40} radius={200} reverse speed={2}>
              {middleOrbit.map((item, index) => (
                <OrbitItemRenderer key={index.toString()} item={item} />
              ))}
            </Orbital>
            <Orbital itemSize={40} radius={280} speed={1}>
              {outerOrbit.map((item, index) => (
                <OrbitItemRenderer key={index.toString()} item={item} />
              ))}
            </Orbital>
          </div>
        </div>
      </div>
    </motion.section>
  )
}

export { CtaOrbitingInstall }
